import { Routes, Route, Navigate, useLocation } from 'react-router-dom'
import { Login } from '~/pages/Login'
import { Callback } from '~/pages/Callback'
import { Home } from '~/pages/Home'
import { ReviewKpis } from '~/pages/ReviewStatus/ReviewKpis'
import { MarketingInvests } from '~/pages/ReviewStatus/MarketingInvests'
import { ReviewStatusIndex } from '~/pages/ReviewStatus'
import { ReviewPerformanceIndex } from '~/pages/ReviewPerformance'
import { CampaignPerformance } from '~/pages/ReviewPerformance/CampaignPerformance'
import { Contribution } from '~/pages/ReviewPerformance/Contribution'
import { Dynamics } from '~/pages/ReviewPerformance/Dynamics'
import { MultiDeepDive } from '~/pages/ReviewPerformance/MultiDeepDive'
import { CollectionList } from '~/pages/Collections/CollectionList'
import { CollectionItem } from '~/pages/Collections/CollectionItem'
import { Profile } from '~/pages/Profile'
import { AccountIndex } from '~/pages/Account'
import { Theming } from '~/pages/Account/Theming'
import { Password } from '~/pages/Account/Password'
import { PrivateRoute } from '~/auth/PrivateRoute'
import { AppLayout } from '~/components/Navigation/AppLayout'
import { ROUTES } from '~/constants/routes'

// Old Dynamics URLs → the merged tab, preserving the query string (carried filter/timeframe state).
function RedirectToDynamics() {
  const { search } = useLocation()
  return <Navigate to={{ pathname: '../dynamics', search }} replace />
}

// Section index → its default tab, preserving the query string. The drawer navigates to the bare
// section path with the carried search; dropping it here would sever filter/timeframe/campaign
// state that tab-nav preserves.
function RedirectToDefaultTab({ to }: { to: string }) {
  const { search } = useLocation()
  return <Navigate to={{ pathname: to, search }} replace />
}

// Old Contribution URLs → the merged page (#13), preserving the query string. driver-contribution
// opens the Driver-groups view: inject view=groups unless the carried search already pins one.
function RedirectToContribution({ groupsView = false }: { groupsView?: boolean }) {
  const { search } = useLocation()
  const params = new URLSearchParams(search)
  if (groupsView && !params.has('view')) params.set('view', 'groups')
  const next = params.toString()
  return <Navigate to={{ pathname: '../contribution', search: next ? `?${next}` : '' }} replace />
}

export function AppRoutes() {
  return (
    <Routes>
      <Route path={ROUTES.login} element={<Login />} />
      <Route path={ROUTES.callback} element={<Callback />} />

      <Route element={<AppLayout />}>
        <Route
          path={ROUTES.home}
          element={
            <PrivateRoute>
              <Home />
            </PrivateRoute>
          }
        />
        <Route
          path={ROUTES.reviewStatus}
          element={
            <PrivateRoute>
              <ReviewStatusIndex />
            </PrivateRoute>
          }
        >
          <Route index element={<RedirectToDefaultTab to="review-kpis" />} />
          <Route path="review-kpis" element={<ReviewKpis />} />
          <Route path="marketing-invests" element={<MarketingInvests />} />
        </Route>
        <Route
          path={ROUTES.reviewPerformance}
          element={
            <PrivateRoute>
              <ReviewPerformanceIndex />
            </PrivateRoute>
          }
        >
          <Route index element={<RedirectToDefaultTab to="campaign-performance" />} />
          <Route path="campaign-performance" element={<CampaignPerformance />} />
          <Route path="contribution" element={<Contribution />} />
          <Route path="marketing-contribution" element={<RedirectToContribution />} />
          <Route path="driver-contribution" element={<RedirectToContribution groupsView />} />
          <Route path="dynamics" element={<Dynamics />} />
          <Route path="campaign-dynamics" element={<RedirectToDynamics />} />
          <Route path="multi-dynamics" element={<RedirectToDynamics />} />
          <Route path="multi-deep-dive" element={<MultiDeepDive />} />
        </Route>
        <Route
          path={ROUTES.collections}
          element={
            <PrivateRoute>
              <CollectionList sharedView={false} />
            </PrivateRoute>
          }
        />
        <Route
          path={ROUTES.collectionsShared}
          element={
            <PrivateRoute>
              <CollectionList sharedView={true} />
            </PrivateRoute>
          }
        />
        <Route
          path="/collections/:collectionId"
          element={
            <PrivateRoute>
              <CollectionItem />
            </PrivateRoute>
          }
        />
        <Route
          path={ROUTES.profile}
          element={
            <PrivateRoute>
              <Profile />
            </PrivateRoute>
          }
        />
        <Route
          path={ROUTES.account}
          element={
            <PrivateRoute>
              <AccountIndex />
            </PrivateRoute>
          }
        >
          <Route index element={<Navigate to="theming" replace />} />
          <Route path="theming" element={<Theming />} />
          <Route path="password" element={<Password />} />
        </Route>
        <Route path="*" element={<Navigate to={ROUTES.home} replace />} />
      </Route>
    </Routes>
  )
}
